import { ServiceMark, serviceForBankName } from '@/components/ds'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import { CopyButton } from '@/components/ui/copy-button'
import { SettingsPage, SettingsSection } from '@/containers/Settings/SettingsLayout'
import { apiFetcher } from '@/data/fetcher'

type BankNotificationsSettings = {
  forwardingEmail: string | null
  supportedBanks: string[]
}

export default function SettingsBankNotificationsPage() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings',
  })

  const { data, isLoading } = useSWR<BankNotificationsSettings>('/api/v2/settings/bank-notifications', apiFetcher)

  const steps = t('bankNotifications.howItWorks.steps', {
    returnObjects: true,
  }) as string[]

  return (
    <SettingsPage
      title="Párování plateb z e-mailu"
      description={t('bankNotifications.description')}
      breadcrumbHref="/settings/bank-notifications"
    >
      {isLoading && <Loading />}

      {data && (
        <>
          <SettingsSection
            title={t('bankNotifications.forwardingEmail.title')}
            description={t('bankNotifications.forwardingEmail.description')}
          >
            {data.forwardingEmail ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted py-1.5 pr-1.5 pl-3">
                <code className="min-w-0 flex-1 truncate font-mono text-sm">{data.forwardingEmail}</code>
                <CopyButton content={data.forwardingEmail} variant="outline" size="icon" />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('bankNotifications.forwardingEmail.unavailable')}</p>
            )}
          </SettingsSection>

          <SettingsSection title={t('bankNotifications.supportedBanks.title')}>
            {data.supportedBanks.length > 0 ? (
              <ul className="flex flex-wrap gap-1.5">
                {data.supportedBanks.map((bank) => (
                  <li key={bank} className="inline-flex h-8 items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-[13px]">
                    {serviceForBankName(bank) ? <ServiceMark service={serviceForBankName(bank)!} size="sm" className="rounded-full" /> : <span className="w-1" />}
                    {bank}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t('bankNotifications.supportedBanks.empty')}</p>
            )}
          </SettingsSection>

          <SettingsSection title={t('bankNotifications.howItWorks.title')}>
            <ol className="space-y-3">
              {steps.map((step, i) => (
                <li key={step} className="flex gap-3 text-sm">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full border text-xs text-muted-foreground tabular">
                    {i + 1}
                  </span>
                  <span className="pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </SettingsSection>
        </>
      )}
    </SettingsPage>
  )
}
